import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEffect, useState } from 'react';
import { GitHubTeam } from '../../types';

export function useFetchUserTeams() {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [teams, setTeams] = useState<GitHubTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let mounted = true;

    async function fetchTeams() {
      try {
        setLoading(true);

        // Get the current user's identity
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;

        // Fetch the user entity to get their teams (memberOf)
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);

        if (!mounted) return;

        if (!userEntity) {
          setTeams([]);
          setLoading(false);
          return;
        }

        // Extract teams from memberOf relations
        const memberOf =
          userEntity.relations?.filter(r => r.type === 'memberOf') ?? [];

        // For GitHub teams, we need to extract the team slug from the entity reference
        // Team entities typically have the format: group:default/team-slug
        const teamSlugs = memberOf
          .map(relation => {
            const entityRef = relation.targetRef;
            const parts = entityRef.split('/');
            if (parts.length === 2 && parts[0].startsWith('group:')) {
              return parts[1]; // This is the team slug
            }
            return null;
          })
          .filter((slug): slug is string => slug !== null);

        // Fetch team entities to get full names
        const teamEntities = await Promise.all(
          memberOf.map(async relation => {
            try {
              return await catalogApi.getEntityByRef(relation.targetRef);
            } catch {
              return null;
            }
          }),
        );

        if (!mounted) return;

        const teamsData: GitHubTeam[] = teamEntities
          .filter(
            (entity): entity is NonNullable<typeof entity> => entity !== null,
          )
          .filter(entity => entity.kind === 'Group')
          .map((entity, index) => {
            const profile = entity.spec?.profile;
            const displayName =
              typeof profile === 'object' &&
              profile !== null &&
              'displayName' in profile
                ? String(profile.displayName)
                : undefined;
            return {
              slug: teamSlugs[index] ?? entity.metadata.name,
              name: displayName ?? entity.metadata.name,
            };
          });

        setTeams(teamsData);
      } catch (err) {
        if (!mounted) return;
        setError(err as Error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchTeams();

    return () => {
      mounted = false;
    };
  }, [catalogApi, identityApi]);

  return { teams, loading, error };
}
